import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { FormDown, FormUp } from 'grommet-icons';
import './AccessibleSelect.css';

/**
 * AccessibleSelect - Componente select accessibile
 * Gestisce correttamente l'apertura/chiusura del dropdown sia con mouse che tastiera
 * ed è completamente accessibile agli screen reader
 */
const AccessibleSelect = (props) => {
  const {
    id,
    className = '',
    options = [],
    value = '',
    placeholder = 'Seleziona',
    onChange,
    disabled = false,
    'aria-labelledby': ariaLabelledBy,
    'aria-required': ariaRequired,
    a11yTitle
  } = props;

  // Stati del componente
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [flatOptions, setFlatOptions] = useState([]);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  // Riferimenti DOM
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const liveRegionRef = useRef(null);
  
  // Crea un ID univoco per il dropdown se non fornito
  const dropdownId = id ? `${id}-dropdown` : `select-dropdown-${Math.floor(Math.random() * 1000)}`;
  const labelId = ariaLabelledBy || (id ? `${id}-label` : undefined);

  /**
   * Appiattisce le opzioni raggruppate in un unico array
   */
  const flattenOptions = (optionsArray) => {
    if (!optionsArray || !Array.isArray(optionsArray) || optionsArray.length === 0) {
      return [];
    }

    let result = [];
    
    // Controlla se le opzioni sono raggruppate
    const hasGroups = optionsArray.some(opt => opt && typeof opt === 'object' && Array.isArray(opt.options));
    
    if (hasGroups) {
      // Estrae le opzioni dai gruppi
      optionsArray.forEach(group => {
        if (group && Array.isArray(group.options)) {
          result = [...result, ...group.options];
        }
      });
    } else {
      // Usa direttamente le opzioni non raggruppate
      result = [...optionsArray];
    }
    
    return result;
  };

  /**
   * Estrae il testo da visualizzare da un'opzione
   */
  const getOptionText = (option) => {
    if (option === null || option === undefined) {
      return '';
    }
    
    if (typeof option === 'object') {
      return option.label || option.value || String(option);
    }
    
    return String(option);
  };
  
  /**
   * Inizializza le opzioni all'avvio e quando cambiano
   */
  useEffect(() => {
    const flattened = flattenOptions(options);
    setFlatOptions(flattened);
    
    // Se non c'è un'opzione selezionata e il dropdown è aperto, imposta il focus sulla prima opzione
    if (isOpen && focusedIndex === -1 && flattened.length > 0) {
      setFocusedIndex(0);
    }
  }, [options, isOpen]);
  
  /**
   * Aggiunge/rimuove listener per i click esterni
   */
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target) && isOpen) {
        closeDropdown();
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);
  
  /**
   * Annuncia messaggi allo screen reader
   */
  const announce = (message) => {
    if (!message) return;
    
    setLiveAnnouncement('');
    
    // Per forzare gli screen reader a rilevare il cambiamento
    setTimeout(() => {
      setLiveAnnouncement(message);
    }, 100);
  };

  /**
   * Apre il dropdown
   */
  const openDropdown = () => {
    if (disabled) return;
    
    console.log('Aprendo dropdown');
    setIsOpen(true);
    
    // Imposta il focus sul primo elemento o sull'elemento selezionato
    const selectedIndex = value ? flatOptions.findIndex(opt => 
      JSON.stringify(opt) === JSON.stringify(value) || opt === value
    ) : 0;
    
    setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    
    // Annuncia l'apertura allo screen reader
    const optionsCount = flatOptions.length;
    announce(`Dropdown aperto. ${optionsCount} opzioni disponibili.`);
  };

  /**
   * Chiude il dropdown
   */
  const closeDropdown = () => {
    console.log('Chiudendo dropdown');
    setIsOpen(false);
    setFocusedIndex(-1);
    
    // Riporta il focus sul container
    if (containerRef.current) {
      containerRef.current.focus();
    }
    
    // Annuncia la chiusura allo screen reader
    announce('Dropdown chiuso');
  };

  /**
   * Toggle apertura/chiusura del dropdown
   */
  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;
    
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };
  
  /**
   * Gestisce la selezione di un'opzione
   */
  const handleSelect = (option, index) => {
    if (disabled) return;
    
    console.log('Selezionata opzione:', option);
    
    // Chiudi il dropdown
    closeDropdown();
    
    // Annuncia la selezione
    const optionText = getOptionText(option);
    announce(`Selezionato: ${optionText}`);
    
    // Chiama l'onChange se definito
    if (onChange) {
      onChange({ option, value: option });
    }
  };
  
  /**
   * Gestisce la navigazione con tastiera
   */
  const handleKeyDown = (event) => {
    if (disabled) return;
    
    console.log('Keydown:', event.key);
    
    switch(event.key) {
      case 'Enter':
      case ' ':  // Spazio
        event.preventDefault();
        
        if (!isOpen) {
          openDropdown();
        } else if (focusedIndex >= 0 && focusedIndex < flatOptions.length) {
          handleSelect(flatOptions[focusedIndex], focusedIndex);
        }
        break;
        
      case 'Escape':
        event.preventDefault();
        
        if (isOpen) {
          closeDropdown();
        }
        break;
        
      case 'Tab':
        // Comportamento standard: chiude il dropdown ma non previene la navigazione tab
        if (isOpen) {
          closeDropdown();
        }
        break;
        
      case 'ArrowDown':
        event.preventDefault();
        
        if (!isOpen) {
          openDropdown();
        } else {
          // Passa all'opzione successiva, con ciclo
          const nextIndex = focusedIndex < flatOptions.length - 1 ? focusedIndex + 1 : 0;
          setFocusedIndex(nextIndex);
          
          // Annuncia l'opzione corrente
          announce(getOptionText(flatOptions[nextIndex]));
        }
        break;
        
      case 'ArrowUp':
        event.preventDefault();
        
        if (!isOpen) {
          openDropdown();
          // Se si apre con freccia su, posiziona all'ultima opzione
          setFocusedIndex(flatOptions.length - 1);
        } else {
          // Passa all'opzione precedente, con ciclo
          const prevIndex = focusedIndex > 0 ? focusedIndex - 1 : flatOptions.length - 1;
          setFocusedIndex(prevIndex);
          
          // Annuncia l'opzione corrente
          announce(getOptionText(flatOptions[prevIndex]));
        }
        break;
        
      case 'Home':
        if (isOpen && flatOptions.length > 0) {
          event.preventDefault();
          setFocusedIndex(0);
          announce(getOptionText(flatOptions[0]));
        }
        break;
        
      case 'End':
        if (isOpen && flatOptions.length > 0) {
          event.preventDefault();
          const lastIndex = flatOptions.length - 1;
          setFocusedIndex(lastIndex);
          announce(getOptionText(flatOptions[lastIndex]));
        }
        break;
        
      default:
        // Navigazione alfabetica
        if (isOpen && /^[a-zA-Z0-9]$/.test(event.key)) {
          const char = event.key.toLowerCase();
          const matchIndex = flatOptions.findIndex((option, index) => 
            index > focusedIndex && getOptionText(option).toLowerCase().startsWith(char)
          );
          
          if (matchIndex >= 0) {
            setFocusedIndex(matchIndex);
            announce(getOptionText(flatOptions[matchIndex]));
          } else {
            // Cerca dall'inizio se non trova corrispondenze dopo l'indice corrente
            const fromStartIndex = flatOptions.findIndex(option => 
              getOptionText(option).toLowerCase().startsWith(char)
            );
            
            if (fromStartIndex >= 0) {
              setFocusedIndex(fromStartIndex);
              announce(getOptionText(flatOptions[fromStartIndex]));
            }
          }
        }
        break;
    }
  };
  
  // Determine display text
  const displayText = value ? getOptionText(value) : placeholder;
  
  return (
    <Box 
      ref={containerRef}
      id={id}
      className={`accessible-select ${className}`}
      tabIndex={disabled ? -1 : 0}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-owns={isOpen ? dropdownId : undefined}
      aria-controls={isOpen ? dropdownId : undefined}
      aria-labelledby={labelId}
      aria-required={ariaRequired}
      aria-disabled={disabled}
      aria-label={a11yTitle}
      style={{ 
        position: 'relative',
        outline: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1
      }}
    >
      {/* Area selezione visibile */}
      <Box
        direction="row"
        align="center"
        justify="between"
        pad="small"
        background={disabled ? { color: '#f0f0f0' } : { color: 'white' }}
        border={{ color: 'brand', size: '1px' }}
        round="small"
      >
        <Text color={value ? 'black' : '#999'} style={{ whiteSpace: 'nowrap', lineHeight: 'normal' }}>{displayText}</Text>
        {isOpen ? <FormUp color="brand" /> : <FormDown color="brand" />}
      </Box>
      
      {/* Dropdown delle opzioni */}
      {isOpen && flatOptions.length > 0 && (
        <Box
          ref={dropdownRef}
          id={dropdownId}
          role="listbox"
          aria-labelledby={labelId}
          tabIndex="-1"
          background="white"
          border={{ color: 'light-3', size: '1px' }}
          style={{
            position: 'absolute',
            width: '100%',
            maxHeight: '250px',
            overflowY: 'auto',
            zIndex: 10,
            top: 'calc(100% + 2px)',
            left: 0
          }}
        >
          {flatOptions.map((option, index) => (
            <Box
              key={`option-${index}`}
              id={`${dropdownId}-option-${index}`}
              role="option"
              aria-selected={value === option}
              pad={{ vertical: 'medium', horizontal: 'small' }}
              background={focusedIndex === index ? { color: '#f8f8f8' } : { color: 'white' }}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(option, index);
              }}
              onMouseEnter={() => setFocusedIndex(index)}
              style={{
                cursor: 'pointer',
                outline: focusedIndex === index ? '2px solid #2684FF' : 'none',
                borderBottom: index < flatOptions.length - 1 ? '1px solid #f0f0f0' : 'none'
              }}
            >
              <Text>{getOptionText(option)}</Text>
            </Box>
          ))}
        </Box>
      )}
      
      {/* Live region per annunci screen reader */}
      <div
        ref={liveRegionRef}
        role="status"
        aria-live="assertive"
        aria-relevant="additions text"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          margin: '-1px',
          padding: 0,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          border: 0
        }}
      >
        {liveAnnouncement}
      </div>
    </Box>
  );
};

AccessibleSelect.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  options: PropTypes.array,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  'aria-labelledby': PropTypes.string,
  'aria-required': PropTypes.string,
  a11yTitle: PropTypes.string
};

export default AccessibleSelect;
